import { useLoanComparatorStore } from '@/app/stores/loan/loan-comparator.store';
import type { IBorrower, ILoan, ILoanQuote, IProspectInfo, IQuotationFormOptions } from '@/app/models/loanQuote';
import {
  PROJECT_PURPOSES,
  PROJECT_PURPOSE_PROFESSIONAL,
  PROJECT_PURPOSE_RENTAL,
  PROJECT_PURPOSE_RESIDENTIAL,
  PROJECT_TYPE_NEW_LOAN,
  PROJECT_TYPE_SWITCHING,
} from '@/app/constants/loanTypes';
import { BANK_OTHER } from '@/app/schemas/loan-comparator/financing.schema';
import { trackChatEvent } from './analytics';
import { loadSession, saveSession } from './session';
import type { CollectedField, CollectedFieldName, CollectedQuoteData, ConversationState, CoverageValue } from './types';

export type LoanStoreApi = Pick<typeof useLoanComparatorStore, 'getState'>;

export interface PrefillResult {
  applied: CollectedFieldName[];
  estimated: CollectedFieldName[];
  skipped: CollectedFieldName[];
}

export interface ChatPrefillMark {
  estimate: boolean;
}

export const CONVERSATION_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;

export const isConversationId = (value: unknown): value is string =>
  typeof value === 'string' && CONVERSATION_ID_PATTERN.test(value);

const ESTIMATE_SUFFIX = ':estimate';

export const encodePrefilledFields = (result: PrefillResult): string[] => [
  ...result.applied,
  ...result.estimated.map((name) => `${name}${ESTIMATE_SUFFIX}`),
];

export const isPrefilledByChat = (fields: readonly string[], name: CollectedFieldName): boolean =>
  fields.includes(name);

export const isChatEstimate = (fields: readonly string[], name: CollectedFieldName): boolean =>
  fields.includes(`${name}${ESTIMATE_SUFFIX}`);

export const chatPrefillMark = (fields: readonly string[], name: CollectedFieldName): ChatPrefillMark | null =>
  isPrefilledByChat(fields, name) ? { estimate: isChatEstimate(fields, name) } : null;

const PURPOSE_BY_LOAN_TYPE: Record<string, number> = {
  residential: PROJECT_PURPOSE_RESIDENTIAL,
  rental: PROJECT_PURPOSE_RENTAL,
  professional: PROJECT_PURPOSE_PROFESSIONAL,
};

// The backend stores guarantees as lowercase legacy codes; ITT and IPT share 'ipt'. Unemployment has no code.
const OPTION_BY_COVERAGE: Partial<Record<CoverageValue, string>> = {
  death: 'dc',
  ptia: 'ptia',
  ipt: 'ipt',
  itt: 'ipt',
};

// Conversation state rides along with the collected data but never lands in the form.
const STATE_FIELDS: CollectedFieldName[] = ['projectStatus', 'projectStatusAt', 'declinedFields', 'qualificationAskedAt', 'collectionOpenedAt'];

const isKnown = <T>(field: CollectedField<T> | null | undefined): field is CollectedField<T> =>
  !!field && field.value !== null && field.value !== undefined && field.value !== '';

const hasValue = (field: CollectedQuoteData[CollectedFieldName]): boolean =>
  !!field && field.value !== null && field.value !== undefined && field.value !== '';

const isoToDDMMYYYY = (iso: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : null;
};

const monthsElapsedSince = (iso: string, today: Date): number => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return 0;
  const months = (today.getFullYear() - Number(match[1])) * 12 + (today.getMonth() + 1 - Number(match[2]));
  return Math.max(0, months);
};

const normalizeName = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const findByName = <T>(items: readonly T[], name: (item: T) => string, wanted: string): T | undefined => {
  const target = normalizeName(wanted);
  if (!target) return undefined;
  return (
    items.find((item) => normalizeName(name(item)) === target) ??
    items.find((item) => {
      const candidate = normalizeName(name(item));
      return candidate.includes(target) || target.includes(candidate);
    })
  );
};

const hasAnsweredProjectType = (quote: ILoanQuote): boolean =>
  quote.project_progression != null || (!!quote.type_quote && quote.type_quote !== PROJECT_TYPE_NEW_LOAN);

const hasBirthdate = (borrower: IBorrower): boolean =>
  !!borrower.birthdate || !!borrower.prospect_info?.birthday_date;

const hasJob = (borrower: IBorrower): boolean => !!borrower.job?.value || !!borrower.type_work?.id;

// Symfony LoanQuote::STATUS_SIMULATOR and STATUS_PROJECT_STEP_VISITED: the visitor has not left step 1 yet.
const PROJECT_STEP_STATUSES: readonly number[] = [0, 15];

// Symfony creates quotes with an empty, deactivated co-borrower: on step 1 that is a default, not an answer.
const hasChosenBorrowers = (quote: ILoanQuote): boolean => {
  const co = quote.co_borrower;
  if (co?.activated === null || co?.activated === undefined) return false;
  if (co.activated) return true;
  return hasBirthdate(co) || hasJob(co) || !PROJECT_STEP_STATUSES.includes(quote.status);
};

// Applies each known field to the loan store only where the visitor has not typed anything yet.
export function applyCollectedToLoanStore(
  collected: CollectedQuoteData,
  store: LoanStoreApi = useLoanComparatorStore,
  today: Date = new Date(),
): PrefillResult {
  const result: PrefillResult = { applied: [], estimated: [], skipped: [] };
  const state = store.getState();
  const quote = state.quote;
  const known = (Object.keys(collected) as CollectedFieldName[]).filter(
    (name) => hasValue(collected[name]) && !STATE_FIELDS.includes(name),
  );
  if (!quote) {
    result.skipped = known;
    return result;
  }

  const apply = (name: CollectedFieldName, isEstimate: boolean) => {
    result.applied.push(name);
    if (isEstimate) result.estimated.push(name);
  };
  const skip = (name: CollectedFieldName) => result.skipped.push(name);
  const formOptions: IQuotationFormOptions | null = state.formOptions;

  const main = quote.main_borrower;
  const mainUpdates: Partial<IBorrower> = {};

  if (isKnown(collected.birthDate)) {
    const formatted = isoToDDMMYYYY(collected.birthDate.value);
    if (formatted && !hasBirthdate(main)) {
      mainUpdates.birthdate = formatted;
      mainUpdates.prospect_info = { birthday_date: formatted } as IProspectInfo;
      apply('birthDate', collected.birthDate.isEstimate);
    } else {
      skip('birthDate');
    }
  }

  if (isKnown(collected.smoker)) {
    if (!main.smoker) {
      mainUpdates.smoker = collected.smoker.value;
      apply('smoker', collected.smoker.isEstimate);
    } else {
      skip('smoker');
    }
  }

  if (isKnown(collected.profession)) {
    const job = hasJob(main) ? undefined : findByName(formOptions?.jobList ?? [], (j) => j.name, collected.profession.value);
    if (job) {
      mainUpdates.job = { label: job.name, value: job.id };
      mainUpdates.type_work = { id: job.id, name: job.name };
      apply('profession', collected.profession.isEstimate);
    } else {
      skip('profession');
    }
  }

  if (isKnown(collected.coverage)) {
    const codes = Array.from(
      new Set(collected.coverage.value.map((c) => OPTION_BY_COVERAGE[c]).filter((c): c is string => !!c)),
    );
    if (codes.length && !(main.options ?? []).length) {
      mainUpdates.options = codes;
      apply('coverage', collected.coverage.isEstimate);
    } else {
      skip('coverage');
    }
  }

  const isDefaultProportion = (borrower: IBorrower | undefined) => !borrower?.proportion || borrower.proportion === 100;

  if (isKnown(collected.proportionMain)) {
    if (isDefaultProportion(main)) {
      mainUpdates.proportion = collected.proportionMain.value;
      apply('proportionMain', collected.proportionMain.isEstimate);
    } else {
      skip('proportionMain');
    }
  }

  if (Object.keys(mainUpdates).length) state.borrower.updateMain(mainUpdates);

  let coActivated = quote.co_borrower?.activated;
  if (isKnown(collected.hasCoBorrower)) {
    if (!hasChosenBorrowers(quote)) {
      if (collected.hasCoBorrower.value) state.borrower.addCoBorrower();
      else state.borrower.deactivateCoBorrower();
      coActivated = collected.hasCoBorrower.value;
      apply('hasCoBorrower', collected.hasCoBorrower.isEstimate);
    } else {
      skip('hasCoBorrower');
    }
  }

  const coUpdates: Partial<IBorrower> = {};
  const coBorrower = () => store.getState().quote?.co_borrower;

  if (isKnown(collected.coBirthDate)) {
    const formatted = isoToDDMMYYYY(collected.coBirthDate.value);
    const co = coBorrower();
    if (coActivated && co && formatted && !hasBirthdate(co)) {
      coUpdates.birthdate = formatted;
      coUpdates.prospect_info = { ...co.prospect_info, birthday_date: formatted } as IProspectInfo;
      apply('coBirthDate', collected.coBirthDate.isEstimate);
    } else {
      skip('coBirthDate');
    }
  }

  if (isKnown(collected.coSmoker)) {
    const co = coBorrower();
    if (coActivated && co && !co.smoker) {
      coUpdates.smoker = collected.coSmoker.value;
      apply('coSmoker', collected.coSmoker.isEstimate);
    } else {
      skip('coSmoker');
    }
  }

  if (isKnown(collected.coProfession)) {
    const co = coBorrower();
    const job = coActivated && co && !hasJob(co)
      ? findByName(formOptions?.jobList ?? [], (j) => j.name, collected.coProfession.value)
      : undefined;
    if (job) {
      coUpdates.job = { label: job.name, value: job.id };
      coUpdates.type_work = { id: job.id, name: job.name };
      apply('coProfession', collected.coProfession.isEstimate);
    } else {
      skip('coProfession');
    }
  }

  if (Object.keys(coUpdates).length) state.borrower.updateCo(coUpdates);

  if (isKnown(collected.proportionCo)) {
    if (coActivated && isDefaultProportion(store.getState().quote?.co_borrower)) {
      state.borrower.updateCo({ proportion: collected.proportionCo.value });
      apply('proportionCo', collected.proportionCo.isEstimate);
    } else {
      skip('proportionCo');
    }
  }

  const quoteUpdates: Partial<ILoanQuote> = {};

  if (isKnown(collected.loanType)) {
    const purpose = PURPOSE_BY_LOAN_TYPE[collected.loanType.value];
    const label = PROJECT_PURPOSES.find((p) => p.id === purpose)?.label;
    if (purpose && label && quote.project_purpose == null) {
      quoteUpdates.project_purpose = purpose;
      quoteUpdates.type_insurance = { id: purpose, name: label };
      apply('loanType', collected.loanType.isEstimate);
    } else {
      skip('loanType');
    }
  }

  const projectType = isKnown(collected.projectType) ? collected.projectType.value : null;
  if (isKnown(collected.projectType)) {
    if (!hasAnsweredProjectType(quote) && (projectType === 'newLoan' || projectType === 'switching')) {
      const typeQuote = projectType === 'switching' ? PROJECT_TYPE_SWITCHING : PROJECT_TYPE_NEW_LOAN;
      quoteUpdates.type_quote = typeQuote;
      quoteUpdates.project_progression = typeQuote;
      quoteUpdates.is_loan_repurchase = false;
      apply('projectType', collected.projectType.isEstimate);
    } else {
      skip('projectType');
    }
  }

  if (isKnown(collected.currentInsurer)) {
    if (!quote.current_insurance_company) {
      quoteUpdates.current_insurance_company = collected.currentInsurer.value;
      quoteUpdates.delegation = true;
      apply('currentInsurer', collected.currentInsurer.isEstimate);
    } else {
      skip('currentInsurer');
    }
  }

  const loanLineFields: CollectedFieldName[] = ['loanAmount', 'remainingMonths', 'rate', 'startDate', 'bank'];
  const wantsLoanLine = loanLineFields.some((name) => hasValue(collected[name]));
  if (wantsLoanLine && !(quote.loans ?? []).length) state.loan.add();
  const loan: ILoan | undefined = store.getState().quote?.loans?.[0];
  const loanUpdates: Partial<ILoan> = {};

  if (isKnown(collected.loanAmount)) {
    if (loan && !loan.sum) {
      loanUpdates.sum = Math.round(collected.loanAmount.value);
      apply('loanAmount', collected.loanAmount.isEstimate);
    } else {
      skip('loanAmount');
    }
  }

  if (isKnown(collected.remainingMonths)) {
    if (loan && !loan.period) {
      const isSwitching = (quoteUpdates.type_quote ?? quote.type_quote) === PROJECT_TYPE_SWITCHING || projectType === 'switching';
      // The form asks for the initial duration: on an existing loan add back the months already repaid.
      const elapsed = isSwitching && isKnown(collected.startDate) ? monthsElapsedSince(collected.startDate.value, today) : 0;
      loanUpdates.period = Math.round(collected.remainingMonths.value) + elapsed;
      apply('remainingMonths', collected.remainingMonths.isEstimate || elapsed > 0);
    } else {
      skip('remainingMonths');
    }
  }

  if (isKnown(collected.rate)) {
    if (loan && !loan.rate) {
      loanUpdates.rate = collected.rate.value;
      apply('rate', collected.rate.isEstimate);
    } else {
      skip('rate');
    }
  }

  if (isKnown(collected.startDate)) {
    const formatted = isoToDDMMYYYY(collected.startDate.value);
    if (loan && formatted && !loan.new_date_loan) {
      loanUpdates.new_date_loan = formatted;
      quoteUpdates.date_contract_begin = formatted;
      apply('startDate', collected.startDate.isEstimate);
    } else {
      skip('startDate');
    }
  }

  if (isKnown(collected.bank)) {
    const hasBank = !!quote.bank_name || !!loan?.bank?.name;
    if (loan && !hasBank) {
      const listed = findByName(
        [...(formOptions?.bankList ?? []), ...(formOptions?.otherBankList ?? [])],
        (name) => name,
        collected.bank.value,
      );
      if (listed || !formOptions) {
        quoteUpdates.bank_name = listed ?? collected.bank.value;
      } else {
        quoteUpdates.bank_name = BANK_OTHER;
        loanUpdates.custom_bank_name = collected.bank.value;
      }
      apply('bank', collected.bank.isEstimate);
    } else {
      skip('bank');
    }
  }

  if (loan && Object.keys(loanUpdates).length) state.loan.update(loan.id, loanUpdates);
  if (Object.keys(quoteUpdates).length) state.quoteActions.update(quoteUpdates);

  return result;
}

export const conversationIdFromQuery = (search?: string): string | null => {
  const qs = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const id = new URLSearchParams(qs).get('chat');
  return isConversationId(id) ? id : null;
};

const STATE_FETCH_TIMEOUT_MS = 4000;

async function fetchConversationState(conversationId: string): Promise<ConversationState | null> {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), STATE_FETCH_TIMEOUT_MS) : null;
  try {
    const res = await fetch(`/api/poc-guide-chat/conversations/${conversationId}/state`, {
      cache: 'no-store',
      signal: controller?.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<ConversationState> | null;
    if (!data || typeof data !== 'object') return null;
    return {
      conversationId: data.conversationId ?? conversationId,
      history: Array.isArray(data.history) ? data.history : [],
      collected: data.collected && typeof data.collected === 'object' ? data.collected : {},
      funnelStage: data.funnelStage ?? null,
      lastActivityAt: data.lastActivityAt ?? null,
    };
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Records the intent so the prefill survives the token redirects and server refreshes that drop `?chat=`.
export function rememberPrefillIntent(conversationId: string, collected: CollectedQuoteData): void {
  const current = loadSession();
  const previous = current.prefill?.conversationId === conversationId ? current.prefill : null;
  saveSession({
    conversationId,
    collected,
    prefill: { conversationId, collected, trackedQuoteTokens: previous?.trackedQuoteTokens ?? [] },
  });
}

export const pendingPrefill = () => loadSession().prefill ?? null;

// Reads `?chat=<conversationId>`, loads the conversation state and returns the collected quote data.
export async function collectedFromQuery(
  search?: string,
): Promise<{ conversationId: string; collected: CollectedQuoteData; state: ConversationState | null } | null> {
  const conversationId = conversationIdFromQuery(search);
  if (!conversationId) return null;
  const state = await fetchConversationState(conversationId);
  const cached = pendingPrefill();
  const collected = state?.collected ?? (cached?.conversationId === conversationId ? cached.collected : null);
  if (!collected) return null;
  rememberPrefillIntent(conversationId, collected);
  if (state?.history.length) saveSession({ history: state.history });
  return { conversationId, collected, state };
}

// Applies the pending intent to the current quote; idempotent, so it can run after every server refresh.
export function runChatPrefill(store: LoanStoreApi = useLoanComparatorStore): PrefillResult | null {
  const intent = pendingPrefill();
  const token = store.getState().quote?.token;
  if (!intent || !token) return null;

  const result = applyCollectedToLoanStore(intent.collected, store);
  if (!result.applied.length) return result;

  const state = store.getState();
  const merged = new Set([...state.prefilledByChat, ...encodePrefilledFields(result)]);
  state.prefillActions.setPrefilledByChat(Array.from(merged));

  if (!intent.trackedQuoteTokens.includes(token)) {
    saveSession({ prefill: { ...intent, trackedQuoteTokens: [...intent.trackedQuoteTokens, token] } });
    trackChatEvent('chat.quote.started', {
      conversationId: intent.conversationId,
      prefilledFields: result.applied,
      estimatedFields: result.estimated,
      quoteToken: token,
    });
  }
  return result;
}

// A server refresh swaps every sub-object at once; an immer edit from a form only touches one of them.
export const isQuoteReplaced = (next: ILoanQuote | null, prev: ILoanQuote | null): boolean =>
  !!next &&
  !!prev &&
  next !== prev &&
  next.main_borrower !== prev.main_borrower &&
  next.loans !== prev.loans;
